import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { FaShoppingCart, FaUser, FaSignOutAlt, FaBell } from 'react-icons/fa';
import { notificationsAPI } from '../utils/api';
import { sanitizeMarkdownHtml } from '../utils/sanitizeHtml';

const HeaderContainer = styled.header`
  background-color: #323232;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  
  img {
    height: 50px;
    width: auto;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 768px) {
    display: ${props => props.isMenuOpen ? 'flex' : 'none'};
    position: absolute;
    top: 70px;
    left: 0;
    right: 0;
    background: #323232;
    flex-direction: column;
    padding: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    gap: 20px;
  }
`;

const NavLink = styled(Link)`
  color: white;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: #ff6b35;
  }
`;

const AuthButtons = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }
`;

const AuthButton = styled.button`
  padding: 8px 12px;
  border-radius: 20px;

  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  
  background-color: transparent;
  color: #ffffff;
  &:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
  }
  
  &.profile {
    background-color: transparent;
    color: #ffffff;
  }
  
  &.profile:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
  }
  
  &.logout {
    background-color: transparent;
    color: #ffffff;
  }
  
  &.logout:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: #ff6b35;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    gap: 6px;
  }
`;

const CartButton = styled.button`
  position: relative;
  background-color: transparent;
  color: #ff6b35;
  padding: 8px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  
  &:hover {
    background: rgba(255, 107, 53, 0.1);
    transform: scale(1.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  .cart-count {
    position: absolute;
    top: -5px;
    right: -5px;
    background-color: #f44336;
    color: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: bold;
  }
  
  .cart-text {
    display: none;
  }
  
  @media (max-width: 768px) {
    width: auto;
    height: auto;
    padding: 8px 12px;
    border-radius: 20px;
    gap: 8px;
    
    .cart-text {
      display: inline;
      color: #ffffff;
      font-size: 14px;
      font-weight: 500;
    }
    
    .cart-count {
      position: static;
      top: auto;
      right: auto;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NotificationsWrapper = styled.div`
  position: relative;
`;

const NotificationsButton = styled.button`
  position: relative;
  background-color: transparent;
  color: #ff6b35;
  padding: 8px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  
  &:hover {
    background: rgba(255, 107, 53, 0.1);
    transform: scale(1.1);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &.is-active {
    background: rgba(255, 107, 53, 0.2);
  }
  
  .notifications-text {
    display: none;
  }
  
  @media (max-width: 768px) {
    width: auto;
    height: auto;
    padding: 8px 12px;
    border-radius: 20px;
    gap: 8px;
    
    .notifications-text {
      display: inline;
      color: #ffffff;
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #ef4444;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const NotificationsPopover = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-width: 380px;
  max-width: 420px;
  z-index: 1000;
  overflow: hidden;
  transform-origin: top right;
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-10px)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    right: -10px;
    min-width: 280px;
    max-width: calc(100vw - 40px);
  }
`;

const NotificationsPopoverHeader = styled.div`
  padding: 15px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #374151;
`;

const NotificationsPopoverList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const NotificationsPopoverItem = styled.div`
  position: relative;
  padding: 12px 15px;
  padding-left: ${props => props.isRead ? '15px' : '19px'};
  border-bottom: 1px solid #f3f4f6;
  background-color: ${props => props.isRead ? '#fafafa' : '#f5f5f5'};
  border-left: ${props => props.isRead ? 'none' : '4px solid rgb(255, 107, 53)'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.isRead ? '#f5f5f5' : '#eeeeee'};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const NotificationsPopoverItemContent = styled.div`
  position: relative;
  padding-right: 80px;
`;

const NotificationsPopoverItemTitle = styled.div`
  font-weight: ${props => props.isRead ? '500' : '700'};
  font-size: 14px;
  color: ${props => props.isRead ? '#6b7280' : 'rgb(204, 86, 42)'};
  margin-bottom: 4px;
  line-height: 1.4;
`;

const NotificationsPopoverItemMessage = styled.div`
  font-size: 13px;
  color: ${props => props.isRead ? '#4b5563' : '#374151'};
  margin-bottom: 4px;
  line-height: 1.5;
`;

const NotificationsPopoverItemDate = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 10px;
  color: #9ca3af;
  white-space: nowrap;
`;

const UnreadIndicator = styled.div`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgb(255, 107, 53);
`;

const NotificationsPopoverFooter = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: rgb(255, 107, 53);
  cursor: pointer;
  border-top: 1px solid #f3f4f6;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 107, 53, 0.1);
  }
`;

const NotificationsPopoverEmpty = styled.div`
  padding: 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;



const MAX_VISIBLE_NOTIFICATIONS = 4;

// Функция для парсинга markdown-разметки в простой HTML
const parseMarkdown = (text) => {
  if (!text) return '';
  
  // Сначала экранируем HTML-теги для безопасности
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Применяем форматирование (после экранирования HTML)
  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // Жирный **текст**
    .replace(/\*(.*?)\*/g, '<em>$1</em>')              // Курсив *текст*
    .replace(/~~(.*?)~~/g, '<del>$1</del>')            // Зачеркнутый ~~текст~~
    .replace(/__(.*?)__/g, '<u>$1</u>');               // Подчеркнутый __текст__
  
  // БЕЗОПАСНОСТЬ: Дополнительная санитизация через DOMPurify
  return sanitizeMarkdownHtml(html);
};

const Header = () => {
  const { isAuthenticated, logout } = useAuth();
  const { totalItems } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationsRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated()) return;
    
    try {
      setIsLoadingNotifications(true);
      const response = await notificationsAPI.getRecent();
      setNotifications(response.notifications || []);
      setUnreadCount(response.unread || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated()) {
      fetchNotifications();
      
      // Обновление уведомлений при событиях
      const handleRefresh = () => {
        fetchNotifications();
      };
      
      window.addEventListener('notifications:refresh', handleRefresh);
      
      // Периодическое обновление (каждые 30 секунд)
      const interval = setInterval(fetchNotifications, 30000);
      
      return () => {
        window.removeEventListener('notifications:refresh', handleRefresh);
        clearInterval(interval);
      };
    }
  }, [isAuthenticated, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsNotificationsOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setIsNotificationsOpen(false);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
    setIsNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    // На мобильных устройствах сразу переходим на страницу уведомлений
    if (window.innerWidth <= 768) {
      setIsMenuOpen(false);
      navigate('/dashboard/notifications');
      return;
    }
    setIsNotificationsOpen(prev => !prev);
  };

  const handleViewAllNotifications = () => {
    setIsNotificationsOpen(false);
    navigate('/dashboard/notifications');
  };

  const handleNotificationClick = async (notificationId) => {
    try {
      await notificationsAPI.markRead(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const hasNotifications = notifications.length > 0;

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/" onClick={handleLinkClick}>
          <img src={process.env.PUBLIC_URL + '/lagom-life-logo.png'} alt="Lagom Life" />
        </Logo>
        
        <Nav isMenuOpen={isMenuOpen}>
          <NavLink to="/" onClick={handleLinkClick}>Главная</NavLink>
          <NavLink to="/catalog" onClick={handleLinkClick}>Каталог</NavLink>
          <NavLink to="/experts" onClick={handleLinkClick}>Эксперты</NavLink>
          <NavLink to="/about" onClick={handleLinkClick}>Наши услуги</NavLink>
          <NavLink to="/referral" onClick={handleLinkClick}>Реферальная программа</NavLink>
          <NavLink to="/news" onClick={handleLinkClick}>Новости</NavLink>
          <NavLink to="/contacts" onClick={handleLinkClick}>Контакты</NavLink>
          
          <AuthButtons>
            {isAuthenticated() ? (
              <>
                <NotificationsWrapper ref={notificationsRef}>
                  <NotificationsButton
                    className={isNotificationsOpen ? 'is-active' : ''}
                    onClick={toggleNotifications}
                    aria-label="Уведомления"
                  >
                    <FaBell />
                    <span className="notifications-text">Уведомления</span>
                    {unreadCount > 0 && (
                      <NotificationBadge>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </NotificationBadge>
                    )}
                  </NotificationsButton>
                  <NotificationsPopover isOpen={isNotificationsOpen}>
                    <NotificationsPopoverHeader>
                      <span>Уведомления</span>
                      {isLoadingNotifications && <span style={{ fontSize: '12px', color: '#9ca3af' }}>Загрузка...</span>}
                    </NotificationsPopoverHeader>
                    <NotificationsPopoverList>
                      {hasNotifications ? (
                        notifications.slice(0, MAX_VISIBLE_NOTIFICATIONS).map((notification) => {
                          const isRead = notification.status === 'read';
                          return (
                            <NotificationsPopoverItem
                              key={notification.id}
                              isRead={isRead}
                              onClick={() => handleNotificationClick(notification.id)}
                            >
                              {!isRead && <UnreadIndicator />}
                              <NotificationsPopoverItemContent>
                                <NotificationsPopoverItemTitle isRead={isRead}>
                                  {notification.title}
                                </NotificationsPopoverItemTitle>
                                <NotificationsPopoverItemMessage 
                                  isRead={isRead}
                                  dangerouslySetInnerHTML={{ __html: parseMarkdown(notification.message) }}
                                />
                                <NotificationsPopoverItemDate>
                                  {new Date(notification.created_at).toLocaleString('ru-RU', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </NotificationsPopoverItemDate>
                              </NotificationsPopoverItemContent>
                            </NotificationsPopoverItem>
                          );
                        })
                      ) : (
                        <NotificationsPopoverEmpty>
                          Пока нет уведомлений
                        </NotificationsPopoverEmpty>
                      )}
                    </NotificationsPopoverList>
                    {hasNotifications && (
                      <NotificationsPopoverFooter onClick={handleViewAllNotifications}>
                        Все уведомления
                      </NotificationsPopoverFooter>
                    )}
                  </NotificationsPopover>
                </NotificationsWrapper>
                <AuthButton 
                  className="profile"
                  as={Link}
                  to="/profile"
                  onClick={handleLinkClick}
                >
                  <FaUser />
                  Профиль
                </AuthButton>
                <AuthButton 
                  className="logout"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt />
                  Выйти
                </AuthButton>
              </>
            ) : (
              <>
                <AuthButton 
                  className="login"
                  as={Link}
                  to="/login"
                  onClick={handleLinkClick}
                >
                  Войти
                </AuthButton>
                <AuthButton 
                  className="register"
                  as={Link}
                  to="/register"
                  onClick={handleLinkClick}
                >
                  Регистрация
                </AuthButton>
              </>
            )}
            <CartButton as={Link} to="/cart" onClick={handleLinkClick}>
              <FaShoppingCart />
              <span className="cart-text">Корзина</span>
              {totalItems > 0 && (
                <span className="cart-count">{totalItems}</span>
              )}
            </CartButton>
          </AuthButtons>
        </Nav>
        
        <MobileMenuButton onClick={toggleMenu}>
          {isMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 